import { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, Printer, Lock } from "lucide-react";
import { FuncionarioCombobox } from "@/components/FuncionarioCombobox";
import { JornadaSelect } from "@/components/JornadaSelect";
import { DepartamentoSelect, CargoSelect } from "@/components/DepartamentoSelect";
import { hm } from "@/features/ponto/pontoApi";

interface Marcacao { id: number; dataHora: string; incluidaPorAjuste: boolean; }

interface DiaEspelho {
  data: string;
  minutosTrabalhados: number;
  minutosPrevistos: number;
  minutosPrevistosComIntervalo: number;
  minutosExtras50: number;
  minutosExtras100: number;
  minutosNoturnos: number;
  minutosFalta: number;
  minutosSaldo: number;
  saldoBancoHorasAcumulado: number;
  inconsistente: boolean;
  tipoDiaTexto: string;
  batidas: Marcacao[];
}

interface Espelho {
  idFuncionario: number;
  funcionarioNome: string;
  funcionarioMatricula: string;
  jornadaNome: string;
  dtInicio: string;
  dtFim: string;
  saldoInicialMin: number;
  saldoFinalMin: number;
  totalExtras50Min: number;
  totalExtras100Min: number;
  totalAdicionalNoturnoPonderadoMin: number;
  reflexoDsrMin: number;
  diasUteis: number;
  diasDescanso: number;
  dias: DiaEspelho[];
}

interface LinhaLote {
  idFuncionario: number;
  funcionarioNome: string;
  sMatricula: string;
  sDepartamento: string;
  jornadaNome: string;
  minutosTrabalhados: number;
  minutosPrevistos: number;
  minutosFalta: number;
  totalExtras50Min: number;
  totalExtras100Min: number;
  saldoInicialMin: number;
  saldoFinalMin: number;
  diasInconsistentes: number;
}

/** "2026-10-01T00:00:00" → Date local (sem deslocar o dia pelo fuso). */
const dataLocal = (s: string) => new Date(`${s.slice(0, 10)}T00:00:00`);
const dataBr = (s: string) => format(dataLocal(s), "dd/MM/yyyy");
const hora = (s: string) => s.slice(11, 16);

const hoje = new Date();
const iso = (d: Date) => format(d, "yyyy-MM-dd");
const primeiroDia = iso(new Date(hoje.getFullYear(), hoje.getMonth(), 1));
const ultimoDia = iso(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0));

/** Até 3 pares Entrada/Saída visíveis; marcações extras vão para a última coluna. */
const PARES = 3;

export default function EspelhoPontoPage() {
  const [aba, setAba] = useState("individual");
  const [funcionarioId, setFuncionarioId] = useState(0);
  const [jornadaId, setJornadaId] = useState<number | undefined>(0);
  const [inicio, setInicio] = useState(primeiroDia);
  const [fim, setFim] = useState(ultimoDia);
  const [espelho, setEspelho] = useState<Espelho | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [confirmarFechamento, setConfirmarFechamento] = useState(false);

  // Lote
  const [departamentoId, setDepartamentoId] = useState<number | undefined>();
  const [cargoId, setCargoId] = useState<number | undefined>();
  const [jornadaLote, setJornadaLote] = useState<number | undefined>();
  const [lote, setLote] = useState<LinhaLote[] | null>(null);
  const [carregandoLote, setCarregandoLote] = useState(false);

  const gerar = async (idFunc = funcionarioId, jornada = jornadaId) => {
    if (!idFunc) { toast.warning("Selecione o funcionário."); return; }
    if (fim < inicio) { toast.warning("O fim do período é anterior ao início."); return; }
    setCarregando(true);
    try {
      // Sem saldoInicialMin: o servidor parte do saldo do último fechamento de banco de horas.
      const r = await apiClient.get<Espelho>("/api/rh/ponto/espelho", {
        params: { funcionarioId: idFunc, jornadaId: jornada ?? 0, inicio, fim },
      });
      setEspelho(r.data);
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao gerar o espelho.");
    } finally {
      setCarregando(false);
    }
  };

  const gerarLote = async () => {
    if (fim < inicio) { toast.warning("O fim do período é anterior ao início."); return; }
    setCarregandoLote(true);
    try {
      const r = await apiClient.get<LinhaLote[]>("/api/rh/ponto/espelho/lote", {
        params: { inicio, fim, departamentoId, cargoId, jornadaId: jornadaLote },
      });
      setLote(r.data);
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao gerar os espelhos.");
    } finally {
      setCarregandoLote(false);
    }
  };

  const abrirIndividual = (idFunc: number) => {
    setFuncionarioId(idFunc);
    setJornadaId(0);
    setAba("individual");
    void gerar(idFunc, 0);
  };

  const fecharBanco = async () => {
    setConfirmarFechamento(false);
    if (!espelho) return;
    try {
      // Fecha exatamente o período/funcionário do espelho exibido (não o que está digitado agora).
      const r = await apiClient.post("/api/rh/ponto/banco-horas/fechar", null, {
        params: { funcionarioId: espelho.idFuncionario, inicio: espelho.dtInicio.slice(0, 10), fim: espelho.dtFim.slice(0, 10) },
      });
      toast.success(r.data?.mensagem ?? "Banco de horas fechado.");
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao fechar o banco de horas.");
    }
  };

  const periodo = (
    <>
      <div className="space-y-1">
        <Label>Início</Label>
        <Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} />
      </div>
      <div className="space-y-1">
        <Label>Fim</Label>
        <Input type="date" value={fim} onChange={(e) => setFim(e.target.value)} />
      </div>
    </>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">Espelho de Ponto</h1>
          <p className="text-sm text-muted-foreground">Apuração do período (REP-P) — marcações, extras e banco de horas.</p>
        </div>
        {aba === "individual" && espelho && (
          <div className="flex gap-2 print:hidden">
            <Button variant="outline" onClick={() => setConfirmarFechamento(true)}>
              <Lock className="h-4 w-4 mr-2" />Fechar banco de horas
            </Button>
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />Imprimir / PDF
            </Button>
          </div>
        )}
        {aba === "lote" && lote && lote.length > 0 && (
          <Button variant="outline" className="print:hidden" onClick={() => window.print()}>
            <Printer className="h-4 w-4 mr-2" />Imprimir / PDF
          </Button>
        )}
      </div>

      <Tabs value={aba} onValueChange={setAba}>
        <TabsList className="print:hidden">
          <TabsTrigger value="individual">Por funcionário</TabsTrigger>
          <TabsTrigger value="lote">Fechamento em lote</TabsTrigger>
        </TabsList>

        <TabsContent value="individual" className="space-y-6">
          <Card className="print:hidden">
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 pt-6 items-end">
              <div className="space-y-1 lg:col-span-2">
                <Label>Funcionário</Label>
                <FuncionarioCombobox value={funcionarioId} onChange={(id) => { setFuncionarioId(id); setEspelho(null); }} situacao="todos" />
              </div>
              {periodo}
              <Button onClick={() => gerar()} disabled={carregando} className="w-full">
                <Search className="h-4 w-4 mr-2" />{carregando ? "Gerando…" : "Gerar"}
              </Button>
              <div className="space-y-1 lg:col-span-2">
                <Label>Jornada (simulação)</Label>
                <JornadaSelect value={jornadaId} onChange={setJornadaId} rotuloNenhum="Usar a do cadastro" />
                <p className="text-xs text-muted-foreground">Deixe em "Usar a do cadastro" — outra jornada só simula a apuração.</p>
              </div>
            </CardContent>
          </Card>

          {espelho && (
            <Card>
              <CardHeader>
                <CardTitle className="flex flex-wrap gap-x-6 gap-y-1 text-base font-normal">
                  <span><b>Funcionário:</b> {espelho.funcionarioMatricula ? `${espelho.funcionarioMatricula} — ` : ""}{espelho.funcionarioNome}</span>
                  <span><b>Jornada:</b> {espelho.jornadaNome || "sem jornada"}</span>
                  <span><b>Período:</b> {dataBr(espelho.dtInicio)} a {dataBr(espelho.dtFim)}</span>
                  <span><b>Saldo anterior:</b> {hm(espelho.saldoInicialMin)}</span>
                  <span><b>Saldo banco:</b> {hm(espelho.saldoFinalMin)}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Tipo</TableHead>
                        {Array.from({ length: PARES }, (_, i) => (
                          <TableHead key={i} className="text-center whitespace-nowrap">Ent. {i + 1} / Saí. {i + 1}</TableHead>
                        ))}
                        <TableHead className="text-right">Trabalhado</TableHead>
                        <TableHead className="text-right">Previsto</TableHead>
                        <TableHead className="text-right" title="Previsto incluindo o intervalo">Prev. c/ interv.</TableHead>
                        <TableHead className="text-right">Falta</TableHead>
                        <TableHead className="text-right">Extra 50%</TableHead>
                        <TableHead className="text-right">Extra 100%</TableHead>
                        <TableHead className="text-right">Noturno</TableHead>
                        <TableHead className="text-right">Saldo dia</TableHead>
                        <TableHead className="text-right">Acumulado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {espelho.dias.map((d) => (
                        <TableRow key={d.data} className={d.inconsistente ? "bg-destructive/5" : undefined}>
                          <TableCell className="whitespace-nowrap">
                            {format(dataLocal(d.data), "dd/MM EEE", { locale: ptBR })}
                            {d.inconsistente && <Badge variant="destructive" className="ml-2" title="Marcação ímpar ou fora de ordem">!</Badge>}
                          </TableCell>
                          <TableCell className="whitespace-nowrap">{d.tipoDiaTexto}</TableCell>
                          {Array.from({ length: PARES }, (_, i) => {
                            const extras = i === PARES - 1 ? d.batidas.slice(PARES * 2) : [];
                            const par = [...d.batidas.slice(i * 2, i * 2 + 2), ...extras];
                            return (
                              <TableCell key={i} className="text-center whitespace-nowrap font-mono text-xs">
                                {par.length === 0 ? <span className="text-muted-foreground">—</span> : par.map((b, j) => (
                                  <span key={j} className={b.incluidaPorAjuste ? "text-blue-600" : undefined}
                                    title={b.incluidaPorAjuste ? "Incluída por ajuste aprovado" : undefined}>
                                    {j > 0 && " / "}{hora(b.dataHora)}{b.incluidaPorAjuste && "*"}
                                  </span>
                                ))}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right">{hm(d.minutosTrabalhados)}</TableCell>
                          <TableCell className="text-right">{hm(d.minutosPrevistos)}</TableCell>
                          <TableCell className="text-right">{hm(d.minutosPrevistosComIntervalo)}</TableCell>
                          <TableCell className="text-right">{d.minutosFalta ? hm(d.minutosFalta) : "—"}</TableCell>
                          <TableCell className="text-right">{hm(d.minutosExtras50)}</TableCell>
                          <TableCell className="text-right">{hm(d.minutosExtras100)}</TableCell>
                          <TableCell className="text-right">{hm(d.minutosNoturnos)}</TableCell>
                          <TableCell className="text-right">{hm(d.minutosSaldo)}</TableCell>
                          <TableCell className="text-right">{hm(d.saldoBancoHorasAcumulado)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex flex-wrap gap-x-8 gap-y-1 mt-4 text-sm">
                  <span><b>Dias úteis:</b> {espelho.diasUteis}</span>
                  <span><b>Dias de descanso:</b> {espelho.diasDescanso}</span>
                  <span><b>Falta:</b> {hm(espelho.dias.reduce((s, d) => s + d.minutosFalta, 0))}</span>
                  <span><b>Total extra 50%:</b> {hm(espelho.totalExtras50Min)}</span>
                  <span><b>Total extra 100%:</b> {hm(espelho.totalExtras100Min)}</span>
                  <span><b>Adic. noturno:</b> {hm(espelho.totalAdicionalNoturnoPonderadoMin)}</span>
                  <span><b>Reflexo DSR:</b> {hm(espelho.reflexoDsrMin)}</span>
                </div>
                {espelho.dias.some((d) => d.batidas.some((b) => b.incluidaPorAjuste)) && (
                  <p className="text-xs text-muted-foreground mt-2">* marcação incluída por ajuste aprovado.</p>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="lote" className="space-y-6">
          <Card className="print:hidden">
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 pt-6 items-end">
              {periodo}
              <div className="space-y-1">
                <Label>Departamento</Label>
                <DepartamentoSelect value={departamentoId} onChange={setDepartamentoId} rotuloTodos="Todos" />
              </div>
              <div className="space-y-1">
                <Label>Cargo</Label>
                <CargoSelect value={cargoId} onChange={setCargoId} rotuloTodos="Todos" />
              </div>
              <div className="space-y-1">
                <Label>Jornada</Label>
                <JornadaSelect value={jornadaLote} onChange={setJornadaLote} rotuloTodos="Todas" />
              </div>
              <Button onClick={gerarLote} disabled={carregandoLote} className="w-full">
                <Search className="h-4 w-4 mr-2" />{carregandoLote ? "Gerando…" : "Gerar"}
              </Button>
            </CardContent>
          </Card>

          {lote && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-normal">
                  <b>Período:</b> {dataBr(inicio)} a {dataBr(fim)} · {lote.length} funcionário(s) ativos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Matrícula</TableHead>
                        <TableHead>Funcionário</TableHead>
                        <TableHead>Departamento</TableHead>
                        <TableHead>Jornada</TableHead>
                        <TableHead className="text-right">Trabalhado</TableHead>
                        <TableHead className="text-right">Previsto</TableHead>
                        <TableHead className="text-right">Falta</TableHead>
                        <TableHead className="text-right">Extra 50%</TableHead>
                        <TableHead className="text-right">Extra 100%</TableHead>
                        <TableHead className="text-right">Saldo anterior</TableHead>
                        <TableHead className="text-right">Saldo final</TableHead>
                        <TableHead className="print:hidden"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lote.length === 0 ? (
                        <TableRow><TableCell colSpan={12} className="text-center text-muted-foreground">Nenhum funcionário ativo com esses filtros.</TableCell></TableRow>
                      ) : lote.map((l) => (
                        <TableRow key={l.idFuncionario}>
                          <TableCell>{l.sMatricula || "—"}</TableCell>
                          <TableCell>
                            {l.funcionarioNome}
                            {l.diasInconsistentes > 0 && (
                              <Badge variant="destructive" className="ml-2" title="Dias com marcação ímpar ou fora de ordem">
                                {l.diasInconsistentes} dia(s) !
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>{l.sDepartamento || "—"}</TableCell>
                          <TableCell>{l.jornadaNome || "—"}</TableCell>
                          <TableCell className="text-right">{hm(l.minutosTrabalhados)}</TableCell>
                          <TableCell className="text-right">{hm(l.minutosPrevistos)}</TableCell>
                          <TableCell className="text-right">{hm(l.minutosFalta)}</TableCell>
                          <TableCell className="text-right">{hm(l.totalExtras50Min)}</TableCell>
                          <TableCell className="text-right">{hm(l.totalExtras100Min)}</TableCell>
                          <TableCell className="text-right">{hm(l.saldoInicialMin)}</TableCell>
                          <TableCell className="text-right font-medium">{hm(l.saldoFinalMin)}</TableCell>
                          <TableCell className="print:hidden text-right">
                            <Button variant="link" size="sm" onClick={() => abrirIndividual(l.idFuncionario)}>Ver espelho</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmarFechamento} onOpenChange={setConfirmarFechamento}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fechar o banco de horas?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  <b>{espelho?.funcionarioNome}</b> — período de {espelho ? `${dataBr(espelho.dtInicio)} a ${dataBr(espelho.dtFim)}` : "—"}, saldo final de{" "}
                  <b>{espelho ? hm(espelho.saldoFinalMin) : "—"}</b>.
                </p>
                <p>O fechamento grava o saldo e ele passa a ser o ponto de partida do próximo período. O período fechado não poderá ser recalculado.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={fecharBanco}>Fechar banco de horas</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
